import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from 'react-router-dom';

import get from "lodash/get";
import map from "lodash/map";
import isEmpty from "lodash/isEmpty";
import { useCanView } from "../../../../config/hook/UserPermissions";
import Alert from "../../../../components/form/Alert";

import {
    Dialog,
    Box,
    Typography,
    IconButton,
    Stack,
    Divider,
    Avatar,
    CardMedia,
    ButtonBase,
    Button,
    CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'; 
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';

import Notification from "../../../../components/form/Notification";

import  AttachFilesModalProvider  from "./AttachFilesModalProvider";

import { postFilesProvider } from "../../../../store/masters/thunk/filesProvider/post";
import { getProviderId } from "../../../../store/masters/thunk/provider/getId";
import { deleteFilesProvider } from "../../../../store/masters/thunk/filesProvider/delete";

const FilesModal = ({ __, open, close }) => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const [imgSelect, setImgSelect] = useState({});
    const [__g] = useTranslation("global");
    const [openModal, setOpenModal] = useState(false);
    const [showNoti, setShowNoti] = useState({ open: false, msg: "", variant: "error", action: "post" });
    const [alert, setAlert] = useState({ open: false, title: "", subtitle: "", type: "" });
    const canView = useCanView();

    const current = useSelector((state) => state.masters.provider.detail);
    const post = useSelector(state => state.masters.fileProvider.post);
    const delet = useSelector(state => state.masters.fileProvider.delete);

    const getData = () => {
        dispatch(getProviderId({ id }));
    };

    const reload = () => getData();

    const closeAlert = () => {
        setAlert({ open: false, title: "", subtitle: "", type: "", btn: "" })
    }

    useEffect(() => {
        if (get(current, "data.providersFiles.length") > 0) {
            setImgSelect(get(current, "data.providersFiles.[0]"));
        } else {
            setImgSelect({}); // Resetea a un objeto vacío si no hay archivos
        }
    }, [get(current, "isSuccess", false), open]);

    useEffect(() => {
        if (get(post, "isSuccess", false)) { reload(); setShowNoti({ open: true, msg: "", variant: "success", action: "post" }); }
        if (get(delet, "isSuccess", false)) { reload(); setShowNoti({ open: true, msg: "", variant: "success", action: "delete" }) }
    }, [post, delet]);

    const onClick = (item) => () => {
        setImgSelect(item);
    };

    const onClose = () => {
        setImgSelect({});
        close();
    };

    const getFileIcon = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();
        switch (extension) {
            case 'pdf':
                return <PictureAsPdfIcon sx={{ fontSize: 40, color: 'red' }} />;
            case 'doc':
            case 'docx':
                return <DescriptionIcon sx={{ fontSize: 40, color: 'blue' }} />;
            case 'xls':
            case 'xlsx':
                return <TableChartIcon sx={{ fontSize: 40, color: 'green' }} />;
            case 'jpg':
            case 'jpeg':
            case 'png':
                return null;
            default:
                return <InsertDriveFileIcon sx={{ fontSize: 40, color: 'gray' }} />;
        }
    };

    const HandleUploadFile = (file) => {
        dispatch(postFilesProvider(file));
    };

    const onDelete = (documenteName) => {
        dispatch(deleteFilesProvider(documenteName));
    };

    const renderPreview = (file) => {
        if (!file || !file.url || !file.fileName) { 
            return (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                    backgroundColor: 'lightgray'
                }}>
                    <Typography variant="caption">No hay vista previa disponible.</Typography>
                </Box>
            );
        }
        const fileUrl = file.url + file.fileName;
        const extension = file.fileName.split('.').pop().toLowerCase();

        if (['jpg', 'jpeg', 'png'].includes(extension)) {
            return (
                <CardMedia
                    component="img"
                    image={file.url + file.fileName}
                    alt={`Vista previa de ${file.fileName}`}
                    sx={{ objectFit: "contain", height: "100%", width: "100%" }}
                />
            );
        } else if (extension === 'pdf') {
            return (
                <iframe
                    src={file.url + file.fileName}
                    title={`Vista previa de ${file.fileName}`}
                    style={{ width: '100%', height: '100%' }}
                >
                    Tu navegador no soporta la visualización de PDFs.
                </iframe>
            );
        }

        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column', 
                alignItems: 'center',
                height: '100%',
                width: '100%',
                backgroundColor: 'lightgray'
            }}>
                {getFileIcon(file.fileName)}
                <Typography
                    variant="caption"
                    sx={{ fontFamily: 'Poppins', color: 'gray', mt: 2, textAlign: 'center' }} 
                >
                    {file.fileName} <br />
                    Este documento no se puede previsualizar en el navegador.
                </Typography>
            </Box>
        );
    };

    return (
        <Dialog onClose={onClose} open={open} maxWidth="lg" fullWidth>
            <Box>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <Typography variant="h6" sx={{ mt: 2, mb: 1, ml: 3 }}>
                    {__("labels.4")}
                </Typography>
                <Divider />
                {open && (
                    <Stack direction={{ xs: "column-reverse", md: "row" }} className='p-4'>
                        <Box className="max-w-auto md:max-w-[300px] max-h-[300px] md:max-h-[400px] overflow-scroll overflow-x-hidden">
                            {map(get(current, "data.providersFiles"), (pict, i) => (
                                <ButtonBase className="flex flex-row" onClick={onClick(pict)} key={i}>
                                    <Box>
                                        {getFileIcon(pict.fileName) ? (
                                            <Avatar
                                                className='border border-black rounded'
                                                variant="square"
                                                sx={{ backgroundColor: 'white', border: 'none' }}
                                            >
                                                {getFileIcon(pict.fileName)}
                                            </Avatar>
                                        ) : (
                                            <Avatar
                                                className='border border-black rounded'
                                                variant="square"
                                                alt={`pict-${i + 1}`}
                                                src={pict.url + pict.fileName}
                                                sx={{ backgroundColor: 'white' }}
                                            />
                                        )}
                                    </Box>
                                    <Box className="p-2 text-left">
                                        <Typography className='!leading-[1.2]' component="div" variant="h6">{i + 1}</Typography>
                                        <Typography className='!leading-[1.2] w-[150px] !truncate' component="div" variant="caption">{pict.localPath}</Typography>
                                    </Box>
                                    <IconButton
                                        sx={{ marginLeft: 'auto', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center',
                                            height: '100%'
                                        }}
                                        href={pict.url + pict.fileName} 
                                        download={pict.fileName}
                                    >
                                        <DownloadIcon sx={{ color: 'blue' }} />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => {
                                            if (canView("MAESTROS/PROVEEDORES/ADJUNTOS", "delete")) {
                                                onDelete(pict.fileName)
                                            } else {
                                                setAlert({
                                                    open: true,
                                                    title: "Permiso Denegado.",
                                                    subtitle: (<ul> • No tienes permiso para eliminar el archivo del proveedor.</ul>),
                                                    type: "error",
                                                    btn: __(`${module}.actions.close`),
                                                    func: closeAlert
                                                })
                                            }
                                        }}
                                        sx={{ marginLeft: 'auto', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center',
                                            height: '100%',
                                            color: 'red'
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </ButtonBase>
                            ))}
                        </Box>

                        <Box className='flex-1 max-h-[300px] md:max-h-[400px] h-[400px] mb-4 sm:mb-0 w-full rounded overflow-hidden pl-4'>
                            {!isEmpty(get(current, "data.providersFiles"))
                                ? (
                                    <div className='flex justify-center items-center w-full h-[300px] md:h-full max-h-full max-w-full bg-gray-200'>
                                        {renderPreview(imgSelect)}
                                    </div>
                                ) : (
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: "column",
                                        height: 400,
                                        width: '100%',
                                        justifyContent: "center",
                                        alignItems: "center"
                                    }}>
                                        <Stack justifyContent="center" alignItems="center" spacing={1} pt={2} >
                                            <Typography variant="bml">{__(`table.empty.archives.name`)}</Typography>
                                            <Typography variant="caption">{__(`table.empty.archives.info`)}</Typography>
                                        </Stack>
                                    </Box>
                                )}
                        </Box>
                    </Stack>
                )}
            </Box>

            {(get(post, "isLoading", false) || get(delet, "isLoading", false)) && (
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    backgroundColor: 'white', // Fondo blanco
                    borderRadius: 2,
                    padding: 2,
                    boxShadow: 3
                }}>
                    <CircularProgress />
                    <Typography variant="caption" sx={{ mt: 2 }}>
                        {__("Cargando...")}
                    </Typography>
                </Box>
            )}

            {/* Botón para subir archivos */}
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <Button
                    variant="contained"
                    component="label"
                    color="primary"
                    onClick={() => {
                        if (canView("MAESTROS/PROVEEDORES/ADJUNTOS", "add")) {
                            setOpenModal(true)
                        } else {
                            setAlert({
                                open: true,
                                title: "Permiso Denegado.",
                                subtitle: (<ul> • No tienes permiso para agregar archivos al proveedor.</ul>),
                                type: "error",
                                btn: __(`${module}.actions.close`),
                                func: closeAlert
                            })
                        }
                    }}>
                    Adjuntar archivos
                </Button>
            </Box>

            <AttachFilesModalProvider 
                open={openModal}
                close={() => setOpenModal(false)}
                file={HandleUploadFile}
            />
            <Notification __={__} showNoti={showNoti} setShowNoti={setShowNoti} />
            <Alert
                title={get(alert, "title")}
                subtitle={get(alert, "subtitle")}
                btn1={{ func: get(alert, "func") }}
                btn2={{}}
                type={get(alert, "type")}
                openAlert={get(alert, "open")}
                closeAlert={closeAlert}
            />
        </Dialog>
    );
}

export default FilesModal;
